import type { OrderData } from '../../bot/types/chartData'
import { closesByEntryTime, ticketsByEntryTime, tradePips } from '../../bot/utils/orderPairing'

// Re-exported because this module's own consumers and tests have always reached pips
// through it. The definition moved to bot/utils/orderPairing so the replay chart's order
// lines share the one pairing and the one pip maths.
export { tradePips }

/**
 * One row of the walkthrough's trade pane.
 *
 * There is no money anywhere in this shape, deliberately. A walkthrough answers "does the code
 * enter and exit where I drew?", not "how much would this make" — the moment a balance or a
 * dollar P&L appears, users read it as a performance claim, which is what the Backtest tab is
 * for. P/L is in pips only.
 */
export interface WalkthroughTrade {
  /** 1-based, in entry order — the "#1 / #2" the user sees against the chart markers. */
  ticket: number
  type: 'Buy' | 'Sell'
  entryTs: number
  entry: number
  sl: number | null
  tp: number | null
  exitTs: number | null
  exit: number | null
  /** Signed pips. Realised once closed; floating against the last revealed close while open. */
  pips: number | null
  exitReason?: string
}

export interface WalkthroughTrades {
  open: WalkthroughTrade[]
  closed: WalkthroughTrade[]
}

/**
 * Partition the run's order events into what is open and what has closed as of the playhead.
 *
 * Both lists are derived from the full event set rather than from the chart's windowed orders:
 * the chart prunes orders when it pages, and a trade list that silently loses rows as the user
 * pans would be worse than useless for validation.
 *
 * Pairing is by entry time, not by id — the datafeed's `orderId` is the event id, so an
 * 'opened' and its matching 'closed' carry different ones, but a position's entry time is
 * shared by both events.
 *
 * @param orders     every order event for the run, unix seconds
 * @param playheadTs replay cursor, unix seconds
 * @param pipSize    from backtest_config.pip_size
 * @param markPrice  last revealed close, for floating pips on open trades
 */
export function buildWalkthroughTrades(
  orders: OrderData[],
  playheadTs: number,
  pipSize: number,
  markPrice: number | null,
): WalkthroughTrades {
  const opens = orders
    .filter((o) => o.eventType === 'opened')
    .sort((a, b) => a.time - b.time)

  // entryTime -> the closing event, for every close the playhead has reached.
  const closesByEntry = closesByEntryTime(orders, playheadTs)
  // Numbered over the whole set rather than over what is revealed, so a trade keeps its number
  // as the replay runs. Shared with the chart's order tooltip — see ticketsByEntryTime for why
  // the two cannot each count for themselves.
  const tickets = ticketsByEntryTime(orders)

  const open: WalkthroughTrade[] = []
  const closed: WalkthroughTrade[] = []

  opens.forEach((o, i) => {
    const ticket = tickets.get(o.time) ?? i + 1
    if (o.time > playheadTs) return

    const type: 'Buy' | 'Sell' = o.type === 'BUY' ? 'Buy' : 'Sell'
    const close = closesByEntry.get(o.time)

    if (close) {
      const exitPrice = close.exitPrice ?? close.price
      closed.push({
        ticket,
        type,
        entryTs: o.time,
        entry: close.entryPrice ?? o.price,
        // A closed position has no live stop or target left to show.
        sl: null,
        tp: null,
        exitTs: close.time,
        exit: exitPrice,
        pips: tradePips(o.type, close.entryPrice ?? o.price, exitPrice, pipSize),
        exitReason: close.exitReason,
      })
      return
    }

    open.push({
      ticket,
      type,
      entryTs: o.time,
      entry: o.price,
      sl: o.sl || null,
      tp: o.tp || null,
      exitTs: null,
      exit: null,
      pips: markPrice != null ? tradePips(o.type, o.price, markPrice, pipSize) : null,
      exitReason: undefined,
    })
  })

  // Newest first in both lists: the trade the user is watching is the one that just happened.
  open.sort((a, b) => b.entryTs - a.entryTs)
  closed.sort((a, b) => (b.exitTs ?? 0) - (a.exitTs ?? 0))

  return { open, closed }
}
